// =============================================================================
// Production Forecast — the Manchester V2 forecast tile, lifted out of the
// retired kitchen app (src/boh-kitchen.jsx ProductionForecast /
// SiteForecastSection / DatePickerModal, 19 Sep 2026) so it renders inside the
// ONE Trafford site app now the kitchen is on the premises.
//
// What changed in the lift (everything else is the kitchen code, verbatim):
//   * ONE site, not a fan-out. The kitchen iterated its own SITES map; here the
//     tile forecasts for the site it is mounted on (site.key = "tc"), which is
//     exactly what the kitchen already sent — every forecast read, confirm and
//     undo has always carried site "tc" (Adam's engine keys on the shop).
//   * No local "confirmed today" marker. The kitchen kept a per-tablet
//     appData.forecastConfirmed flag; the server's forecaststatus read is the
//     authority and is re-checked on every mount, so the marker was redundant.
//   * The confirm snapshot now carries the actor (name / role / home site) from
//     the tablet's roster so the Command Centre drill-down satisfies the
//     per-action rule (who, role, home) once FOH and BOH share one site key.
//   * Self-contained styles (its own S + B, like meadowhallForecast.jsx) and a
//     once-injected <style> for the two kitchen.css rules it needs, so the FOH
//     bundle's look is untouched and kitchen.css never loads on a till.
//
// Backend + gates are unchanged: Google ?action=forecast / forecaststatus and
// the forecastConfirm / forecastUndo POSTs (role-gated BOH + Admin by the Apps
// Script matrix), with the Lane BG Supabase mirror behind FORECAST_READ_HOSTS.
// =============================================================================
import React from "react";
import * as forecastReader from "./forecastReader.js"; // Lane BG: forecast ENGINE surface read from Supabase (gated OFF by FORECAST_READ_HOSTS; falls back to the Google ?action=forecast on any failure/stale bench).
const { useState, useEffect, useCallback, useMemo } = React;

const B = {nori:"#00392C",wasabi:"#D4FB6F",kewpie:"#FEFAF0",rice:"#FFFFFF",coral:"#FF7A5C",sunshine:"#F1C544",eucalyptus:"#6FAF8F"};

// UK-local day key (matches foh.jsx todayKey) so the TODAY badge lines up with
// shop closing, not 00:00 UTC.
function todayKey(){
  try{ return new Intl.DateTimeFormat("en-CA",{timeZone:"Europe/London"}).format(new Date()); }
  catch(e){ return new Date().toISOString().slice(0,10); }
}

// The two kitchen.css rules this screen relies on, injected once. `pendingPulse`
// is the awaiting-confirmation glow; the media rules keep the per-roll row on one
// line on a phone. Everything else is inline (S below).
const PF_CSS_ID = "rolled-production-forecast-css";
function ensureCss(){
  if (typeof document === "undefined" || document.getElementById(PF_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = PF_CSS_ID;
  el.textContent =
    "@keyframes pendingPulse{0%,100%{box-shadow:0 0 0 1px rgba(255,122,92,0.22),0 0 18px rgba(255,122,92,0.10)}50%{box-shadow:0 0 0 1px rgba(255,122,92,0.35),0 0 28px rgba(255,122,92,0.18)}}" +
    "@media (max-width:500px){.fc-sku-col-rolls{width:108px !important}.fc-sku-col-portions{width:42px !important}.fc-sku-row .fc-sku-ctrls{width:108px !important;gap:4px !important}.fc-sku-row .fc-sku-portions{width:42px !important}}";
  document.head.appendChild(el);
}

// Styles — the subset of the kitchen app's S that these components use, verbatim.
const S = {
  cBtn:{width:30,height:30,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.wasabi,fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif"},
  cVal:{fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.95rem",color:B.kewpie,opacity:0.4,minWidth:24,textAlign:"center"},
  calDay:{aspectRatio:"1",borderRadius:10,border:"1px solid transparent",background:"none",color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.1s ease"},
  calDaySelected:{background:B.wasabi,color:B.nori,fontWeight:800,border:"1px solid "+B.wasabi,boxShadow:"0 0 18px rgba(212,251,111,0.4)"},
  calDayToday:{background:"rgba(212,251,111,0.08)",border:"1px solid rgba(212,251,111,0.25)",fontWeight:700,color:B.wasabi},
  calWeekday:{fontSize:"0.68rem",fontWeight:700,color:B.kewpie,opacity:0.4,textAlign:"center",letterSpacing:"0.05em",textTransform:"uppercase",padding:"4px 0"},
  calcRow:{display:"flex",justifyContent:"space-between",alignItems:"center"},
  confirmedPill:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(111,175,143,0.1)",border:"1px solid rgba(111,175,143,0.3)",color:B.eucalyptus,fontSize:"0.62rem",fontWeight:700,padding:"3px 10px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",marginLeft:10},
  confirmedWrap:{borderRadius:16,padding:"10px 10px 4px",marginBottom:8,boxShadow:"0 0 0 1px rgba(111,175,143,0.22)",transition:"box-shadow 0.45s ease"},
  content:{padding:"0 20px 24px",maxWidth:640,width:"100%",margin:"0 auto"},
  ctrls:{display:"flex",alignItems:"center",gap:8},
  dateTrigger:{display:"inline-flex",alignItems:"center",gap:10,padding:"10px 18px",borderRadius:14,border:"1px solid rgba(212,251,111,0.18)",background:"rgba(212,251,111,0.05)",color:B.kewpie,cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left",transition:"all 0.15s ease"},
  dateTriggerBadge:{fontSize:"0.6rem",fontWeight:800,color:B.wasabi,background:"rgba(212,251,111,0.12)",borderRadius:999,padding:"3px 10px",marginLeft:8,letterSpacing:"0.1em"},
  flag:{fontSize:"0.72rem",padding:"3px 8px",borderRadius:16,background:"rgba(212,251,111,0.06)",color:B.wasabi},
  flagsAppliedBadge:{position:"absolute",top:-8,left:14,background:B.nori,padding:"0 8px",fontSize:"0.6rem",fontWeight:800,color:B.wasabi,letterSpacing:"0.1em",textTransform:"uppercase",border:"1px solid rgba(212,251,111,0.15)",borderRadius:8,lineHeight:"16px"},
  flagsAppliedCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"12px 16px 14px",marginBottom:16,position:"relative"},
  heroCard:{textAlign:"center",padding:"28px 20px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:14,marginBottom:12,position:"relative"},
  infoBanner:{padding:"10px 14px",borderRadius:10,border:"1px solid",marginBottom:12,fontSize:"0.78rem",lineHeight:1.5},
  itemName:{fontSize:"0.85rem",fontWeight:500,color:B.kewpie},
  loadingBox:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px"},
  markBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(212,251,111,0.25)",background:"rgba(212,251,111,0.08)",color:B.wasabi,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  modal:{background:B.nori,border:"1px solid rgba(212,251,111,0.2)",borderRadius:18,padding:20,maxWidth:380,width:"calc(100% - 32px)",maxHeight:"80vh",overflowY:"auto"},
  modalCancel:{flex:1,padding:12,borderRadius:10,border:"1px solid rgba(254,250,240,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  modalOk:{flex:1,padding:12,borderRadius:10,border:"none",background:B.wasabi,color:B.nori,fontSize:"0.85rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  navBtn:{width:36,height:36,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"},
  overlay:{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",background:"rgba(0,0,0,0.6)",zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",padding:16,boxSizing:"border-box",overflowY:"auto"},
  overrideHint:{fontSize:"0.7rem",color:B.kewpie,opacity:0.35,marginTop:8,textAlign:"center",lineHeight:1.5},
  pendingDot:{width:6,height:6,borderRadius:"50%",background:B.coral,boxShadow:"0 0 6px rgba(255,122,92,0.7)"},
  pendingPill:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.25)",color:B.coral,fontSize:"0.62rem",fontWeight:700,padding:"3px 10px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",marginLeft:10},
  pendingWrap:{borderRadius:16,padding:"10px 10px 4px",marginBottom:8,transition:"box-shadow 0.45s ease",animation:"pendingPulse 3.2s ease-in-out infinite"},
  pill:{fontSize:"0.72rem",padding:"3px 8px",borderRadius:16,background:"rgba(212,251,111,0.06)",color:B.kewpie,opacity:0.6,display:"inline-flex",alignItems:"center",gap:4},
  pillDot:{width:4,height:4,borderRadius:"50%",background:B.wasabi},
  pillWarn:{background:"rgba(255,179,71,0.06)"},
  scaleRow:{display:"flex",justifyContent:"space-between",marginTop:6,padding:"0 2px",fontSize:"0.62rem",color:B.kewpie,opacity:0.35,fontFamily:"Manrope,sans-serif",fontWeight:600,letterSpacing:"0.04em"},
  scaleTick:{flex:1,textAlign:"center"},
  scaleTickEdge:{flex:"0 0 auto"},
  secTitle:{fontSize:"0.7rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8,marginTop:8,display:"flex",alignItems:"center"},
  sectionCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"14px 16px",marginBottom:16},
  siteSectionDot:{width:8,height:8,borderRadius:"50%",background:B.wasabi,boxShadow:"0 0 10px rgba(212,251,111,0.5)"},
  siteSectionHeader:{fontFamily:"gelica,serif",fontSize:"1.3rem",fontWeight:700,color:B.wasabi,padding:"18px 14px 8px",borderTop:"2px solid rgba(212,251,111,0.15)",marginTop:10,marginBottom:4,letterSpacing:"0.01em",display:"flex",alignItems:"center",justifyContent:"center",gap:10,flexWrap:"wrap",rowGap:6,textAlign:"center"},
  skuColHeader:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 12px 10px",fontSize:"0.62rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",borderBottom:"1px solid rgba(212,251,111,0.08)",marginBottom:6},
  skuColHeaderLeft:{flex:1},
  skuColHeaderPortions:{width:60,textAlign:"right"},
  skuColHeaderRolls:{width:140,textAlign:"center"},
  skuPortionsVal:{width:60,textAlign:"right",fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.9rem",color:B.kewpie,opacity:0.55},
  skuRow:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease",gap:8},
  skuRowLeft:{flex:1,minWidth:0},
  spinner:{width:32,height:32,border:"3px solid rgba(212,251,111,0.15)",borderTopColor:B.wasabi,borderRadius:"50%",animation:"spin 0.8s linear infinite"},
  submitBtn:{width:"100%",padding:14,background:B.wasabi,color:B.nori,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:700,border:"none",borderRadius:14,cursor:"pointer",letterSpacing:"0.02em"},
  todayJumpBtn:{background:"none",border:"1px solid rgba(212,251,111,0.2)",color:B.wasabi,fontSize:"0.7rem",fontWeight:700,padding:"5px 14px",borderRadius:999,cursor:"pointer",fontFamily:"Manrope,sans-serif",letterSpacing:"0.06em",textTransform:"uppercase"},
};

// ===========================================
// PRODUCTION FORECAST — one site (the one this app is mounted on)
// ===========================================
export function ProductionForecast({site,backend,requestPin,staffByPin}){
  useEffect(()=>{ ensureCss(); },[]);
  const [fcDate,setFcDate] = useState(new Date());
  const [pickerOpen,setPickerOpen] = useState(false);
  const dateStr = fcDate.toISOString().slice(0,10);
  const isToday = dateStr === todayKey();
  const dayName = fcDate.toLocaleDateString("en-GB",{weekday:"long"});
  const dateDisplay = fcDate.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
  const changeDate=(delta)=>{setFcDate(d=>{const n=new Date(d);n.setDate(n.getDate()+delta);return n})};

  return (
    <div className="fc-forecast-page" style={S.content}>
      {/* Shared date nav — arrows for ±1 day, tap to open calendar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:8}}>
        <button style={S.navBtn} onClick={()=>changeDate(-1)} aria-label="Previous day">◀</button>
        <button onClick={()=>setPickerOpen(true)} style={S.dateTrigger} aria-label="Open calendar">
          <span style={{fontSize:"1rem",lineHeight:1,marginRight:4}}>📅</span>
          <span>
            <span style={{display:"block",fontFamily:"gelica,serif",fontWeight:700,fontSize:"1.1rem",color:B.kewpie,lineHeight:1.1}}>{dayName}</span>
            <span style={{display:"block",fontSize:"0.78rem",color:B.kewpie,opacity:0.45,marginTop:2}}>{dateDisplay}</span>
          </span>
          {isToday && <span style={S.dateTriggerBadge}>TODAY</span>}
        </button>
        <button style={S.navBtn} onClick={()=>changeDate(1)} aria-label="Next day">▶</button>
      </div>
      {!isToday && (
        <div style={{textAlign:"center",marginBottom:6}}>
          <button style={S.todayJumpBtn} onClick={()=>setFcDate(new Date())}>Jump to today</button>
        </div>
      )}

      {pickerOpen && <DatePickerModal value={fcDate} onChange={setFcDate} onClose={()=>setPickerOpen(false)}/>}

      <div className="fc-sites-grid">
        <SiteForecastSection site={site.key} siteName={site.name} date={fcDate} dateStr={dateStr} backend={backend} requestPin={requestPin} staffByPin={staffByPin}/>
      </div>
    </div>
  );
}

function DatePickerModal({value,onChange,onClose}){
  const [viewDate,setViewDate] = useState(()=>{ const d=new Date(value); d.setDate(1); return d; });
  const monthLabel = viewDate.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  const firstOfMonth = new Date(viewDate.getFullYear(),viewDate.getMonth(),1);
  const leadMondayOffset = (firstOfMonth.getDay()+6)%7; // 0 for Mon, 6 for Sun
  const gridStart = new Date(firstOfMonth); gridStart.setDate(gridStart.getDate()-leadMondayOffset);
  const days = Array.from({length:42},(_,i)=>{ const d=new Date(gridStart); d.setDate(gridStart.getDate()+i); return d; });
  const sameDay=(a,b)=>a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();
  const today = new Date();
  const prevMonth=()=>{const d=new Date(viewDate);d.setMonth(d.getMonth()-1);setViewDate(d);};
  const nextMonth=()=>{const d=new Date(viewDate);d.setMonth(d.getMonth()+1);setViewDate(d);};
  const pick=(d)=>{onChange(d);onClose();};
  const weekdays = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{...S.modal,maxWidth:400}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button style={S.navBtn} onClick={prevMonth} aria-label="Previous month">◀</button>
          <div style={{fontFamily:"gelica,serif",fontSize:"1.15rem",fontWeight:700,color:B.wasabi}}>{monthLabel}</div>
          <button style={S.navBtn} onClick={nextMonth} aria-label="Next month">▶</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
          {weekdays.map((w,i)=>(<div key={i} style={S.calWeekday}>{w}</div>))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
          {days.map((d,i)=>{
            const inMonth = d.getMonth()===viewDate.getMonth();
            const dToday = sameDay(d,today);
            const dSelected = sameDay(d,value);
            return (
              <button key={i} onClick={()=>pick(d)} style={{...S.calDay,...(inMonth?{}:{opacity:0.22}),...(dToday&&!dSelected?S.calDayToday:{}),...(dSelected?S.calDaySelected:{})}}>{d.getDate()}</button>
            );
          })}
        </div>
        <div style={{display:"flex",gap:8,marginTop:16}}>
          <button style={S.modalCancel} onClick={onClose}>Cancel</button>
          <button style={S.modalOk} onClick={()=>pick(new Date())}>Today</button>
        </div>
      </div>
    </div>
  );
}

// The backend sends each roll's share of the day as a whole-number percent
// (e.g. 15 = 15%). We must NOT guess fraction-vs-percent per roll: a genuine
// low-volume roll at 1% (s.pct === 1) looks identical to a 1.0 fraction, and
// the old `pct<=1 ? pct*100 : pct` test blew every 1% roll up to 100% of the
// whole site. That doubled the site total once the summer specials (Fish &
// Chips, Honey Soy) landed at ~1% each. Decide ONCE across the whole list:
// only treat the values as fractions if the entire list sums to ~1.
export function skuSharePct(sku, allSkus){
  const sum = (allSkus||[]).reduce((a,s)=>a+(Number(s.pct)||0),0);
  const listIsFractions = sum>0 && sum<=1.5;
  return listIsFractions ? (Number(sku.pct)||0)*100 : (Number(sku.pct)||0);
}

function SiteForecastSection({site,siteName,date,dateStr,backend,requestPin,staffByPin}){
  const [forecast,setForecast] = useState(null);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState(null);
  const [showInfo,setShowInfo] = useState(false);
  const [override,setOverride] = useState(0);
  const [skuAdj,setSkuAdj] = useState({});
  const [confirmed,setConfirmed] = useState(false);
  const [confirming,setConfirming] = useState(false);
  const [undoing,setUndoing] = useState(false);
  // Inline error after a failed undo so the user can see the rejection
  // (e.g. "role does not permit this action" when a FOH staffer enters
  // their PIN). Without this, no-cors swallowed the rejection silently.
  const [undoErr,setUndoErr] = useState("");
  // Has this site's forecast already been confirmed today? Null = not checked
  // yet, object = state from backend. Shapes:
  //   { confirmed: true, by, at, portions, rolls, resubmit? }
  //   { confirmed: false, undone: true, undoneBy, undoneAt, previousPortions, previousRolls }
  //   { confirmed: false }
  // false (not null) means we successfully checked and it's pending.
  const [confirmedInfo,setConfirmedInfo] = useState(null);
  // Lane BG: where the forecast was built. null = the Google engine (today's
  // behaviour, and the only value on a non-gated host); "supabase"/"supabase_fallback"
  // only ever appears on a FORECAST_READ_HOSTS host or with ?forecastread=on.
  const [fcSource,setFcSource] = useState(null);
  // Lane BG: an honest secondary message when the Supabase confirm/undo mirror
  // DISAGREES with the Sheet (role refusal or error). Never blocks the Sheet path.
  const [fcShadowNote,setFcShadowNote] = useState("");

  // Re-fetch the confirmedInfo banner state without remounting the form so
  // chefs don't lose in-progress slider/SKU tweaks. Used after a same-session
  // undo so the banner flips from green-confirmed to yellow-undone notice.
  const reloadStatus = useCallback(()=>{
    const dateBE = date.toLocaleDateString("en-GB");
    // Lane BG: on a gated host read the status from Supabase (ISO date — NEVER the
    // en-GB string), falling back to the Google status. OFF branch is unchanged.
    if (forecastReader.forecastReadEnabled()) {
      const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,"0"),d=String(date.getDate()).padStart(2,"0");
      const apply=(data)=>setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false);
      const google=()=>fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE)).then(r=>r.ok?r.json():{confirmed:false});
      forecastReader.kioskStatus({site,date:y+"-"+m+"-"+d}).then(s=>s?apply(s):google().then(apply)).catch(()=>setConfirmedInfo(false));
    } else {
      fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE))
        .then(r=>r.ok?r.json():{confirmed:false})
        .then(data=>{ setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); })
        .catch(()=>{ setConfirmedInfo(false); });
    }
  },[backend,site,date]);

  useEffect(()=>{
    let cancelled=false;
    setLoading(true);setError(null);setForecast(null);setShowInfo(false);setOverride(0);setSkuAdj({});setConfirmed(false);setConfirmedInfo(null);setFcSource(null);setFcShadowNote("");
    const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,"0"),d=String(date.getDate()).padStart(2,"0");
    // Lane BG: on a gated host (FORECAST_READ_HOSTS — empty today) build the forecast
    // from Supabase, falling back to the Google call on any failure/stale bench. The
    // OFF branch below is byte-for-byte today's call, so a non-gated host is unchanged.
    if (forecastReader.forecastReadEnabled()) {
      const iso=y+"-"+m+"-"+d;
      const googleFetch=()=>fetch(backend+"?action=forecast&site="+site+"&date="+iso).then(r=>{if(!r.ok)throw new Error("HTTP "+r.status);return r.json()});
      forecastReader.fetchForecastDay({site,date:iso,googleFetch})
        .then(({data,source})=>{if(!cancelled){if(!data)throw new Error("no forecast");if(data.error)throw new Error(data.error);setForecast(data);setFcSource(source);setLoading(false)}})
        .catch(e=>{if(!cancelled){setError(e.message);setLoading(false)}});
    } else {
      fetch(backend+"?action=forecast&site="+site+"&date="+y+"-"+m+"-"+d)
        .then(r=>{if(!r.ok)throw new Error("HTTP "+r.status);return r.json()})
        .then(data=>{if(!cancelled){if(data.error)throw new Error(data.error);setForecast(data);setLoading(false)}})
        .catch(e=>{if(!cancelled){setError(e.message);setLoading(false)}});
    }
    // Parallel: has today's forecast already been confirmed for this site?
    const dateBE = date.toLocaleDateString("en-GB");
    // Lane BG: on a gated host read the status from Supabase (ISO date — NEVER the
    // en-GB string), falling back to the Google status. OFF branch is unchanged.
    if (forecastReader.forecastReadEnabled()) {
      const applyS=(data)=>{ if(!cancelled) setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); };
      const googleS=()=>fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE)).then(r=>r.ok?r.json():{confirmed:false});
      forecastReader.kioskStatus({site,date:y+"-"+m+"-"+d}).then(s=>s?applyS(s):googleS().then(applyS)).catch(()=>{ if(!cancelled) setConfirmedInfo(false); });
    } else {
      fetch(backend+"?action=forecaststatus&site="+site+"&date="+encodeURIComponent(dateBE))
        .then(r=>r.ok?r.json():{confirmed:false})
        .then(data=>{ if(!cancelled) setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); })
        .catch(()=>{ if(!cancelled) setConfirmedInfo(false); });
    }
    return()=>{cancelled=true};
  },[site,dateStr]);

  // Forecast target portions — drives initial per-SKU roll distribution.
  const calcTarget = ()=>{
    if(!forecast)return 0;
    let b = forecast.forecast.weightedBase, am=1;
    forecast.autoFlags.forEach(f=>{am+=f.mult});
    let adj = b*am;
    if(forecast.wastageAdjustment){adj*=(forecast.wastageAdjustment.type==="trim"?0.95:1.05)}
    adj *= (1+override/100);
    const TB = forecast.forecast.tgtgBuffer;
    let tp = Math.round(adj+TB);
    if(forecast.forecast.minApplied && override===0) tp = forecast.forecast.totalPortions;
    return tp;
  };
  const target = calcTarget();

  // Largest-remainder allocation across historical rolls, PLUS a fixed floor for
  // brand-new specials. A new roll has no same-weekday sales history yet, so the
  // backend returns it at pct 0 (getSixWeekHistory only reads the same weekday, 6
  // weeks back — the specials that launched 01/09 hadn't traded on any of those
  // days). A pure pct-proportional split therefore gives every new roll 0 and the
  // kitchen makes none of them (the 04/09 "new rolls show 0" bug). The backend
  // already reserves each new roll a minimum (newSkuMinimum -> s.rolls); honour
  // that floor here and split the REMAINING rolls across the historical SKUs by
  // share, so the hero total is unchanged — we just stop over-making old rolls.
  const baseRollsBySku = useMemo(()=>{
    if(!forecast||!forecast.skuBreakdown)return {};
    const skus = forecast.skuBreakdown;
    const targetRolls = Math.round(target/2);
    const isNewSku = s => (s.isSpecial||s.isNew) && skuSharePct(s, skus)<=0;
    const out = {};

    // 1) Reserve each new special its backend floor (rolls, or portions/2 if the
    //    older field name comes back). Additive to the historical pct split below.
    let reserved = 0;
    skus.forEach(s=>{
      if(isNewSku(s)){
        const floor = Math.max(0, Math.round(Number(s.rolls) || Math.ceil((Number(s.portions)||0)/2) || 0));
        out[s.name] = floor;
        reserved += floor;
      }
    });

    // 2) Distribute the remaining rolls across historical SKUs by pct share.
    const hist = skus.filter(s=>!isNewSku(s));
    const histTarget = Math.max(0, targetRolls - reserved);
    const raw = hist.map(s=>histTarget*(skuSharePct(s, skus)/100));
    const floors = raw.map(r=>Math.floor(r));
    hist.forEach((s,i)=>{out[s.name]=floors[i]});
    let need = histTarget - floors.reduce((a,b)=>a+b,0);
    if(need>0){
      const rem = raw.map((r,i)=>({r:r-floors[i],i})).sort((a,b)=>b.r-a.r);
      for(let k=0;k<Math.min(need,rem.length);k++) out[hist[rem[k].i].name]++;
    } else if(need<0){
      const rem = raw.map((r,i)=>({r:r-floors[i],i})).sort((a,b)=>a.r-b.r);
      for(let k=0;k<Math.min(-need,rem.length);k++){
        const n=hist[rem[k].i].name;
        if(out[n]>0)out[n]--;
      }
    }
    return out;
  },[forecast,target]);

  const getSkuBase=(name)=>baseRollsBySku[name]||0;

  const totalRolls = Object.values(baseRollsBySku).reduce((a,b)=>a+b,0) + Object.values(skuAdj).reduce((a,b)=>a+b,0);
  const totalPortions = totalRolls*2;

  const adjSku=(name,delta)=>{
    setSkuAdj(prev=>{
      const base = getSkuBase(name);
      const cur = prev[name]||0;
      if(base+cur+delta<0)return prev;
      return {...prev,[name]:cur+delta};
    });
  };

  // Who is confirming — name / role / home site from the tablet's roster, so
  // the snapshot satisfies the per-action rule now FOH and BOH share one site
  // key. The server still stamps the PIN Attempts audit itself; this is the copy
  // the Command Centre drill-down reads. Missing roster entry -> name only.
  const actorFor = (staffName, staffPin)=>{
    const row = (staffByPin && staffPin && staffByPin[staffPin]) || null;
    return { name: staffName || (row && row.name) || "", role: (row && row.role) || "", homeSite: (row && (row.homeSite || row.home)) || "" };
  };

  // Phase 5 Option 2 (2026-04-21): capture a full calculation snapshot so the
  // admin Forecast Submissions drill-down can show the exact roll mix staff
  // saw at confirm time (backend's weighted base, auto flags, wastage trim,
  // per-SKU rolls before + after the user's adjustment). Fires in the same
  // forecastConfirm POST body as a new `snapshot` field.
  const buildSnapshot = (actor)=>{
    if (!forecast) return null;
    const finalSkuRolls = {};
    Object.keys(baseRollsBySku).forEach(k => {
      finalSkuRolls[k] = (baseRollsBySku[k]||0) + (skuAdj[k]||0);
    });
    Object.keys(skuAdj).forEach(k => { if (!(k in finalSkuRolls)) finalSkuRolls[k] = skuAdj[k]; });
    const skuPcts = {};
    (forecast.skuBreakdown||[]).forEach(s => {
      skuPcts[s.name] = skuSharePct(s, forecast.skuBreakdown);
    });
    const fc = forecast.forecast || {};
    const range = (fc.rangeLow != null && fc.rangeHigh != null)
      ? {low: fc.rangeLow, high: fc.rangeHigh, safetyBuffer: fc.safetyBuffer != null ? fc.safetyBuffer : null}
      : null;
    return {
      weightedBase: fc.weightedBase,
      basePredictedPortions: fc.totalPortions,
      tgtgBuffer: fc.tgtgBuffer,
      minApplied: !!fc.minApplied,
      autoFlags: (forecast.autoFlags||[]).map(f => ({label:f.label, mult:f.mult, emoji:f.emoji||"", positive:!!f.positive})),
      wastageAdjustment: forecast.wastageAdjustment ? forecast.wastageAdjustment.type : null,
      wastageAdjustmentDetail: forecast.wastageAdjustment || null,
      overridePct: override,
      confirmedPortions: totalPortions,
      confirmedRolls: totalRolls,
      baseSkuRolls: Object.assign({}, baseRollsBySku),
      userSkuAdjustments: Object.assign({}, skuAdj),
      finalSkuRolls,
      skuPcts,
      inputs: forecast.inputs || null,
      calc: forecast.calc || null,
      range,
      actor: actor || null,
      schemaVersion: 2
    };
  };

  // 2026-05-04 (optimistic): banner state flips on tap, POST runs in
  // background, rollback + inline error on failure. JJ flagged the prior
  // wait-for-fetch-then-flip pattern as feeling broken — this makes the
  // confirm <-> undone <-> resubmitted transitions feel instant.
  const handleConfirm = ()=>{
    requestPin(async(staffName,staffPin)=>{
      const priorInfo = confirmedInfo;
      const priorConfirmed = confirmed;
      const wasUndone = !!(priorInfo && priorInfo.undone);
      const actor = actorFor(staffName, staffPin);
      // Apply the new confirmed state immediately.
      setConfirmedInfo({
        confirmed: true,
        by: staffName,
        at: new Date().toISOString(),
        portions: totalPortions,
        rolls: totalRolls,
        resubmit: wasUndone
      });
      setConfirmed(true);
      setConfirming(true);
      setUndoErr("");
      // Background POST. text/plain skips the CORS preflight Apps Script
      // doesn't answer (memory: apps_script_cors_preflight_trap). Response
      // body still readable so role-gate rejections surface.
      let ok = false, errMsg = "";
      try{
        const r = await fetch(backend,{
          method:"POST",
          headers:{"Content-Type":"text/plain;charset=utf-8"},
          body:JSON.stringify({action:"forecastConfirm",site,date:dateStr,totalPortions,totalRolls,override,skuAdjustments:skuAdj,snapshot:buildSnapshot(actor),staffName,staffPin})
        });
        const result = await r.json().catch(()=>null);
        if (r.ok && result && result.status === "success") ok = true;
        else errMsg = (result && (result.error || result.message)) || ("HTTP " + r.status);
      }catch(e){ errMsg = e.message || "Network error"; }
      setConfirming(false);
      if (!ok) {
        // Roll back the optimistic flip.
        setConfirmedInfo(priorInfo);
        setConfirmed(priorConfirmed);
        setUndoErr(errMsg || "Couldn't confirm — try again or check with admin.");
      } else if (forecastReader.forecastReadEnabled()) {
        // Lane BG: the Sheet is confirmed (still the record). Mirror into the
        // Supabase door (1121) as an honest secondary — never undoes the Sheet
        // confirm, only surfaces a disagreement (role refusal / error). Not fired
        // when the Sheet write failed, so we never show a Supabase success the
        // Sheet refused. staffPin is used only to obtain the login pass.
        const cy=date.getFullYear(),cm=String(date.getMonth()+1).padStart(2,"0"),cd=String(date.getDate()).padStart(2,"0");
        const iso=cy+"-"+cm+"-"+cd;
        const shadow = await forecastReader.kioskConfirm({site,date:iso,portions:totalPortions,rolls:totalRolls,override,skuAdjustments:skuAdj,snapshot:buildSnapshot(actor),idemKey:"kc:"+site+":"+iso+":"+Date.now(),staffPin});
        setFcShadowNote(forecastReader.shadowDisagrees(shadow) ? ("Saved. The mirror copy did not record ("+(shadow.reason||shadow.status)+").") : "");
      }
    });
  };

  // Undo & resubmit (2026-05-04, optimistic). Banner flips to yellow
  // "previously undone" the instant the user taps; POST runs in background;
  // rollback + inline error on failure. Server enforces BOH/Admin role via
  // canPerformAction.
  const handleUndo = ()=>{
    setUndoErr("");
    requestPin(async(staffName,staffPin)=>{
      const priorInfo = confirmedInfo;
      const priorConfirmed = confirmed;
      const undonePortions = (priorInfo && priorInfo.portions != null) ? priorInfo.portions : totalPortions;
      const undoneRolls    = (priorInfo && priorInfo.rolls    != null) ? priorInfo.rolls    : totalRolls;
      // Optimistically apply the undone state.
      setConfirmedInfo({
        confirmed: false,
        undone: true,
        undoneBy: staffName,
        undoneAt: new Date().toISOString(),
        previousPortions: undonePortions,
        previousRolls: undoneRolls
      });
      setConfirmed(false);
      setUndoing(true);
      setUndoErr("");
      // Background POST. See handleConfirm for the text/plain reasoning.
      let ok = false, errMsg = "";
      try{
        const r = await fetch(backend,{
          method:"POST",
          headers:{"Content-Type":"text/plain;charset=utf-8"},
          body:JSON.stringify({
            action:"forecastUndo",
            site,date:dateStr,
            undonePortions, undoneRolls, undoneOverride: override,
            staffName, staffPin
          })
        });
        const result = await r.json().catch(()=>null);
        if (r.ok && result && result.status === "success") ok = true;
        else errMsg = (result && (result.error || result.message)) || ("HTTP " + r.status);
      }catch(e){ errMsg = e.message || "Network error"; }
      setUndoing(false);
      if (!ok) {
        // Roll back: banner returns to whatever was there before.
        setConfirmedInfo(priorInfo);
        setConfirmed(priorConfirmed);
        setUndoErr(errMsg || "Couldn't undo — try again or check with admin.");
      } else if (forecastReader.forecastReadEnabled()) {
        // Lane BG: mirror the undo into the Supabase door (1121) as an honest
        // secondary — never re-does the Sheet undo, only surfaces a disagreement.
        const cy=date.getFullYear(),cm=String(date.getMonth()+1).padStart(2,"0"),cd=String(date.getDate()).padStart(2,"0");
        const iso=cy+"-"+cm+"-"+cd;
        const shadow = await forecastReader.kioskUndo({site,date:iso,idemKey:"ku:"+site+":"+iso+":"+Date.now(),staffPin});
        setFcShadowNote(forecastReader.shadowDisagrees(shadow) ? ("Undone. The mirror copy did not record ("+(shadow.reason||shadow.status)+").") : "");
      }
    });
  };

  const retry=()=>{
    setLoading(true);setError(null);
    const y=date.getFullYear(),m=String(date.getMonth()+1).padStart(2,"0"),d=String(date.getDate()).padStart(2,"0");
    // Lane BG: mirror the initial load's gated path (OFF branch byte-identical to today).
    if (forecastReader.forecastReadEnabled()) {
      const iso=y+"-"+m+"-"+d;
      const googleFetch=()=>fetch(backend+"?action=forecast&site="+site+"&date="+iso).then(r=>r.json());
      forecastReader.fetchForecastDay({site,date:iso,googleFetch})
        .then(({data,source})=>{setForecast(data);setFcSource(source);setLoading(false)}).catch(e=>{setError(e.message);setLoading(false)});
    } else {
      fetch(backend+"?action=forecast&site="+site+"&date="+y+"-"+m+"-"+d)
        .then(r=>r.json()).then(data=>{setForecast(data);setLoading(false)}).catch(e=>{setError(e.message);setLoading(false)});
    }
  };

  const isRemotelyConfirmed = !!(confirmedInfo && confirmedInfo.confirmed);
  const isConfirmed = confirmed || isRemotelyConfirmed;
  const showPending = !!forecast && !loading && !error && !isConfirmed;
  const showConfirmed = !!forecast && !loading && !error && isConfirmed;

  const fmtConfirmedAt = (iso) => {
    try { return new Date(iso).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}); }
    catch(e) { return ""; }
  };

  return (
    <div>
      <div className="fc-site-header" style={S.siteSectionHeader}>
        <span style={S.siteSectionDot}/>
        {siteName}
        {showPending && <span style={S.pendingPill}><span style={S.pendingDot}/>Awaiting confirmation</span>}
        {showConfirmed && <span style={S.confirmedPill}>✓ Confirmed</span>}
        {/* Lane BG source badge — only ever shows on a gated host; OFF hosts never set fcSource. */}
        {(fcSource==="supabase"||fcSource==="supabase_fallback") && <span title="Forecast built from the live record" style={{marginLeft:8,fontSize:"0.6rem",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:B.wasabi,opacity:0.7}}>◆ live{fcSource==="supabase_fallback"?" · fallback":""}</span>}
      </div>

      {isConfirmed && (
        // Single confirmed banner — fires whether the lock-in came from this
        // session (`confirmed`) or another tab/device (`isRemotelyConfirmed`).
        // The Undo & resubmit button gives chefs an escape hatch when the
        // forecast turns out wrong; gated server-side by canPerformAction.
        <div style={{background:"rgba(111,175,143,0.12)",border:"1px solid rgba(111,175,143,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:12,color:"#9DD0B0",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:"1.4rem",flexShrink:0}}>✓</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"0.88rem",color:B.kewpie,letterSpacing:"0.01em"}}>
              {confirmedInfo && confirmedInfo.resubmit
                ? "Forecast resubmitted"
                : (isRemotelyConfirmed ? "Forecast already confirmed" : siteName + " forecast confirmed")}
              {confirmedInfo && confirmedInfo.by ? " by " + confirmedInfo.by : ""}
              {confirmedInfo && confirmedInfo.at ? " at " + fmtConfirmedAt(confirmedInfo.at) : ""}
            </div>
            <div style={{fontSize:"0.75rem",opacity:0.75,marginTop:2}}>
              {confirmedInfo && confirmedInfo.portions != null
                ? <>Locked in at <strong>{confirmedInfo.portions} portions</strong>{confirmedInfo.rolls != null && <> / {confirmedInfo.rolls} rolls</>}. </>
                : null}
              Mistake? <strong>Undo &amp; resubmit</strong> to edit and re-confirm.
            </div>
          </div>
          <button
            onClick={handleUndo}
            disabled={undoing}
            style={{flexShrink:0,padding:"8px 14px",borderRadius:10,border:"1px solid rgba(255,179,71,0.4)",background:"rgba(255,179,71,0.08)",color:B.sunshine,fontFamily:"inherit",fontSize:"0.78rem",fontWeight:700,letterSpacing:"0.02em",cursor:undoing?"wait":"pointer"}}
          >
            {undoing?"Undoing…":"↩ Undo & resubmit"}
          </button>
          {undoErr && (
            <div style={{flexBasis:"100%",fontSize:"0.78rem",color:B.coral,marginTop:6,padding:"8px 10px",borderRadius:8,background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.3)"}}>
              Undo failed: {undoErr}
            </div>
          )}
        </div>
      )}

      {!isConfirmed && confirmedInfo && confirmedInfo.undone && (
        <div style={{background:"rgba(255,179,71,0.07)",border:"1px solid rgba(255,179,71,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:12,color:B.sunshine,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:"1.4rem",flexShrink:0}}>↩</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"0.88rem",color:B.kewpie,letterSpacing:"0.01em"}}>
              Last submission undone{confirmedInfo.undoneBy?" by "+confirmedInfo.undoneBy:""}{confirmedInfo.undoneAt?" at "+fmtConfirmedAt(confirmedInfo.undoneAt):""}
            </div>
            <div style={{fontSize:"0.75rem",opacity:0.85,marginTop:2}}>
              {confirmedInfo.previousPortions!=null && <>Was <strong>{confirmedInfo.previousPortions} portions</strong>{confirmedInfo.previousRolls!=null && <> / {confirmedInfo.previousRolls} rolls</>}. </>}
              Review the numbers and confirm again.
            </div>
          </div>
        </div>
      )}

      {loading && <div style={S.loadingBox}><div style={S.spinner}/><div style={{marginTop:12,color:B.kewpie,opacity:0.4,fontSize:"0.82rem"}}>Loading {siteName} forecast…</div></div>}
      {error && <div style={{...S.sectionCard,borderColor:"rgba(255,122,92,0.3)"}}><div style={{color:B.coral,fontSize:"0.85rem",fontWeight:600}}>Failed to load forecast</div><div style={{color:B.kewpie,opacity:0.5,fontSize:"0.78rem",marginTop:4}}>{error}</div><button style={{...S.markBtn,marginTop:10}} onClick={retry}>Retry</button></div>}

      {forecast && !loading && !error && (<div style={confirmed?S.confirmedWrap:S.pendingWrap}>
        {/* Hero — live total from per-SKU commitments */}
        <div style={S.heroCard}>
          <div style={{fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"3px",textTransform:"uppercase",marginBottom:4}}>TOTAL PORTIONS</div>
          <div style={{fontFamily:"gelica,serif",fontSize:"3.5rem",fontWeight:700,color:B.wasabi,lineHeight:1,letterSpacing:"-1px"}}>{totalPortions}</div>
          <div style={{fontSize:"0.82rem",color:B.kewpie,opacity:0.5,marginTop:4}}>{totalRolls} rolls to make</div>
          <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.3,marginTop:6}}>Incl. {forecast.forecast.tgtgBuffer} TGTG buffer</div>
          <button style={{position:"absolute",top:10,right:10,width:24,height:24,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.2)",background:"none",color:B.kewpie,opacity:0.4,fontSize:"0.7rem",fontStyle:"italic",cursor:"pointer"}} onClick={()=>setShowInfo(!showInfo)}>i</button>
        </div>

        {/* Data sources */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>DATA SOURCES</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            {forecast.inputs.lastSameDay!==null
              ? <span style={S.pill}><span style={S.pillDot}/> Last {forecast.day}: <strong>{forecast.inputs.lastSameDay}</strong></span>
              : <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> Last {forecast.day}: <strong>baseline</strong></span>}
            {forecast.inputs.sixWeekAvg!==null
              ? <span style={S.pill}><span style={S.pillDot}/> 6wk avg: <strong>{forecast.inputs.sixWeekAvg}</strong> ({forecast.inputs.weeksOfData}wk)</span>
              : <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> 6wk avg: <strong>baseline</strong></span>}
            {forecast.inputs.lastWastage>0 && <span style={S.pill}><span style={S.pillDot}/> Wastage: <strong>{forecast.inputs.lastWastage}%</strong></span>}
          </div>
        </div>

        {/* Previous day wastage — read-only echo of yesterday's EOD total
            with the per-SKU mix so production can spot the patterns. */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>PREVIOUS DAY WASTAGE <span style={{marginLeft:6,color:B.kewpie,opacity:0.3,fontWeight:400,textTransform:"none",letterSpacing:0}}>(read-only)</span></div>
          {forecast.inputs.previousDayWaste && forecast.inputs.previousDayWaste.totalPortions!=null ? (
            <>
              <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap",marginBottom:forecast.inputs.previousDayWaste.items&&forecast.inputs.previousDayWaste.items.length?10:0}}>
                <span style={{fontFamily:"gelica,serif",fontSize:"1.8rem",fontWeight:700,color:forecast.inputs.previousDayWaste.totalPortions===0?B.wasabi:B.kewpie,lineHeight:1}}>{forecast.inputs.previousDayWaste.totalPortions}</span>
                <span style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.5}}>portion{forecast.inputs.previousDayWaste.totalPortions===1?"":"s"} wasted on {forecast.inputs.previousDayWaste.date}</span>
              </div>
              {forecast.inputs.previousDayWaste.items && forecast.inputs.previousDayWaste.items.length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {forecast.inputs.previousDayWaste.items.map(it=>(
                    <span key={it.name} style={S.pill}><span style={S.pillDot}/> {it.name}: <strong>{it.qty}</strong></span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.4,fontStyle:"italic"}}>No EOD report submitted yesterday</div>
          )}
        </div>

        {/* Context flags — already applied to the total above */}
        {forecast.autoFlags.length>0 && (
          <div style={S.flagsAppliedCard}>
            <div style={S.flagsAppliedBadge}>✓ Already applied</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,marginBottom:8,marginTop:4,lineHeight:1.5}}>These factors are baked into the total above — no further action needed.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {forecast.autoFlags.map((f,i)=>(
                <span key={i} style={{...S.flag,...(f.positive?{}:{background:"rgba(255,179,71,0.06)",color:B.sunshine})}}>{f.emoji} {f.label} {f.mult>0?"+":""}{Math.round(f.mult*100)}%</span>
              ))}
              {override!==0&&<span style={S.flag}>🔧 Override {override>0?"+":""}{override}%</span>}
            </div>
          </div>
        )}

        {/* Wastage warning */}
        {forecast.wastageAdjustment && (
          <div style={{...S.infoBanner,borderColor:forecast.wastageAdjustment.type==="trim"?"rgba(255,122,92,0.2)":"rgba(212,251,111,0.15)",background:forecast.wastageAdjustment.type==="trim"?"rgba(255,122,92,0.05)":"rgba(212,251,111,0.04)",color:forecast.wastageAdjustment.type==="trim"?B.coral:B.wasabi}}>
            {forecast.wastageAdjustment.type==="trim"?"⚠":"ℹ️"} {forecast.wastageAdjustment.message}
          </div>
        )}

        {/* Min floor notice */}
        {forecast.forecast.minApplied && override===0 && (
          <div style={{...S.infoBanner,borderColor:"rgba(212,251,111,0.12)",background:"rgba(212,251,111,0.04)",color:B.wasabi}}>
            ℹ️ Minimum floor applied ({forecast.forecast.totalPortions} portions)
          </div>
        )}

        {/* Info panel */}
        {showInfo && forecast.calc && (
          <div style={{...S.sectionCard,marginBottom:8}}>
            <div style={S.secTitle}>MODEL BREAKDOWN</div>
            <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.6,lineHeight:2}}>
              <div style={S.calcRow}><span>Last same-day (×{forecast.calc.lastSameDayWeight})</span><strong style={{color:B.kewpie}}>{forecast.calc.lastSameDaySignal}</strong></div>
              <div style={S.calcRow}><span>6wk avg (×{forecast.calc.sixWeekAvgWeight})</span><strong style={{color:B.kewpie}}>{forecast.calc.sixWeekAvgSignal}</strong></div>
              <div style={S.calcRow}><span>Baseline (×{forecast.calc.baselineWeight})</span><strong style={{color:B.kewpie}}>{forecast.calc.baselineSignal}</strong></div>
              <div style={{...S.calcRow,borderTop:"1px solid rgba(212,251,111,0.1)",paddingTop:4,color:B.wasabi}}><span>Weighted base</span><strong>{forecast.forecast.weightedBase}</strong></div>
              <div style={S.calcRow}><span>TGTG buffer</span><strong>+{forecast.forecast.tgtgBuffer}</strong></div>
              <div style={{...S.calcRow,color:B.wasabi}}><span>Total</span><strong>{totalPortions}</strong></div>
            </div>
          </div>
        )}

        {/* Override slider — 1% steps, ruled scale, direction labels */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"2px",textTransform:"uppercase"}}>MANUAL OVERRIDE</span>
            <span style={{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:override!==0?B.wasabi:B.kewpie,opacity:override!==0?1:0.3,transition:"color 0.2s"}}>{override>0?"+":""}{override}%</span>
          </div>
          <input type="range" min="-30" max="30" step="1" value={override} onChange={e=>setOverride(Number(e.target.value))} style={{width:"100%",accentColor:B.wasabi,display:"block"}}/>
          <div style={S.scaleRow}>
            <span style={S.scaleTickEdge}>−30%</span>
            <span style={S.scaleTick}>−15%</span>
            <span style={S.scaleTick}>0</span>
            <span style={S.scaleTick}>+15%</span>
            <span style={S.scaleTickEdge}>+30%</span>
          </div>
          <div style={S.overrideHint}>← less than forecast &nbsp;·&nbsp; more than forecast →</div>
        </div>

        {/* SKU breakdown — rolls adjustable, portions auto-derived */}
        {forecast.skuBreakdown && forecast.skuBreakdown.length>0 && (
          <div style={{...S.sectionCard,marginBottom:8}}>
            <div style={S.secTitle}>PER-SKU BREAKDOWN</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.4,marginBottom:10,lineHeight:1.5}}>Adjust rolls (1 roll = 2 portions). The total above updates live.</div>
            <div style={S.skuColHeader}>
              <span style={S.skuColHeaderLeft}>Roll type</span>
              <span className="fc-sku-col-rolls" style={S.skuColHeaderRolls}>Rolls</span>
              <span className="fc-sku-col-portions" style={S.skuColHeaderPortions}>Portions</span>
            </div>
            {forecast.skuBreakdown.map(s=>{
              const base=getSkuBase(s.name);
              const adj=skuAdj[s.name]||0;
              const rolls=base+adj;
              const portions=rolls*2;
              return (
                <div key={s.name} className="fc-sku-row" style={S.skuRow}>
                  <div style={S.skuRowLeft}>
                    <span style={S.itemName}>{s.name}</span>{s.isSpecial&&<span style={{fontSize:"0.55rem",color:B.sunshine,fontWeight:700,marginLeft:6,letterSpacing:"1px"}}>NEW</span>}
                    <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.3}}>{s.pct.toFixed(1)}%</div>
                  </div>
                  <div className="fc-sku-ctrls" style={{...S.ctrls,width:140,justifyContent:"center"}}>
                    <button style={S.cBtn} onClick={()=>adjSku(s.name,-1)}>−</button>
                    <span style={{...S.cVal,...(adj!==0?{color:adj>0?B.wasabi:B.sunshine,opacity:1}:{})}}>{rolls}</span>
                    <button style={S.cBtn} onClick={()=>adjSku(s.name,1)}>+</button>
                  </div>
                  <div className="fc-sku-portions" style={S.skuPortionsVal}>{portions}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Confirm — disabled when already confirmed locally OR by someone else on a different device */}
        <button style={{...S.submitBtn,...(isConfirmed?{opacity:0.6}:{}),marginBottom:10}} onClick={handleConfirm} disabled={isConfirmed||confirming}>
          {confirming
            ? "Confirming…"
            : confirmed
              ? "✓ "+siteName+" Confirmed"
              : isRemotelyConfirmed
                ? "✓ Already confirmed"
                : (confirmedInfo && confirmedInfo.undone
                    ? "Resubmit "+siteName+" Forecast"
                    : "Confirm "+siteName+" Forecast")}
        </button>
        {/* Lane BG: honest secondary note when the Supabase mirror disagreed with the Sheet. */}
        {fcShadowNote && <div style={{fontSize:"0.72rem",color:B.sunshine,opacity:0.85,marginTop:2,marginBottom:8}}>ℹ️ {fcShadowNote}</div>}
      </div>)}
    </div>
  );
}
